import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSpinner, FaChartPie, FaBrain, FaArrowLeft } from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RiskProfiler = ({ clientId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const [answers, setAnswers] = useState({
    q1: 0,
    q2: 0,
    q3: 0,
    q4: 0
  });

  const questions = [
    {
      id: 'q1',
      question: 'What is your primary goal for this investment?',
      options: [
        { value: 0, label: 'Safety of Capital' },
        { value: 3, label: 'Balance of Safety & Growth' },
        { value: 5, label: 'Maximize Long-term Growth' }
      ]
    },
    {
      id: 'q2',
      question: 'If your portfolio drops 20% in a month, what would you do?',
      options: [
        { value: 0, label: 'Sell immediately to prevent loss' },
        { value: 3, label: 'Hold and wait for recovery' },
        { value: 5, label: 'Buy more at lower prices' }
      ]
    },
    {
      id: 'q3',
      question: 'How many years until you need this money?',
      options: [
        { value: 0, label: 'Less than 3 years' },
        { value: 3, label: '3 to 7 years' },
        { value: 5, label: 'More than 7 years' }
      ]
    },
    {
      id: 'q4',
      question: 'What is your experience with equity markets?',
      options: [
        { value: 0, label: 'No experience / FD only' },
        { value: 3, label: 'Some mutual funds' },
        { value: 5, label: 'Active stock investor' }
      ]
    }
  ];

  useEffect(() => {
    fetchAssessment();
  }, [clientId]);

  const fetchAssessment = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/risk-assessment/${clientId}`);
      setAssessment(response.data);
      setAnswers({
        q1: response.data.q1_investment_goal,
        q2: response.data.q2_portfolio_drop,
        q3: response.data.q3_time_horizon,
        q4: response.data.q4_market_experience
      });
    } catch (error) {
      if (error.response?.status !== 404) {
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const params = new URLSearchParams({
        client_id: clientId,
        q1: answers.q1,
        q2: answers.q2,
        q3: answers.q3,
        q4: answers.q4
      });

      const response = await axios.post(`${API}/risk-assessment?${params}`);
      setAssessment(response.data);
      
      // Fetch AI insights
      fetchAIInsights();
    } catch (error) {
      alert('Failed to save risk assessment');
    } finally {
      setSaving(false);
    }
  };

  const fetchAIInsights = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.get(`${API}/risk-assessment/${clientId}/ai-insights`);
      setAiInsights(response.data);
    } catch (error) {
    } finally {
      setLoadingAI(false);
    }
  };

  const chartData = assessment ? [
    { name: 'Equity', value: assessment.equity_allocation, color: '#FFD700' },
    { name: 'Debt', value: assessment.debt_allocation, color: '#1E3A5F' }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] mb-2">📊 Risk Profiler Assessment</h2>
        <p className="text-gray-300">Understand your investment personality</p>
      </div>

      {/* Questions */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {questions.map((q, index) => (
          <div key={q.id} className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <p className="text-white font-semibold mb-4">
              {index + 1}. {q.question}
            </p>
            <div className="space-y-2">
              {q.options.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center p-4 rounded-lg cursor-pointer transition-all ${
                    answers[q.id] === option.value
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold'
                      : 'bg-[#0A1929] text-gray-300 hover:bg-[#1E3A5F]'
                  }`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    value={option.value}
                    checked={answers[q.id] === option.value}
                    onChange={() => handleAnswerChange(q.id, option.value)}
                    className="mr-3"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all transform hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Calculating...
            </>
          ) : (
            'Calculate Risk Profile'
          )}
        </button>
      </form>

      {/* Results */}
      {assessment && (
        <div className="space-y-6">
          {/* Risk Profile Card */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#FFD700] mb-4">Risk Profile</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-400 text-sm mb-2">Profile Type</p>
                <p className="text-3xl font-bold text-white mb-4">{assessment.risk_profile}</p>
                
                <p className="text-gray-400 text-sm mb-2">Risk Score</p>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-[#0A1929] rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] h-3 rounded-full transition-all"
                      style={{ width: `${(assessment.risk_score / 20) * 100}%` }}
                    />
                  </div>
                  <span className="text-[#FFD700] font-bold">{assessment.risk_score}/20</span>
                </div>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-4">Recommended Asset Allocation</p>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <p className="text-center text-white mt-2">
                  <span className="text-[#FFD700] font-bold">{assessment.equity_allocation}%</span> Equity | 
                  <span className="text-[#1E3A5F] font-bold"> {assessment.debt_allocation}%</span> Debt
                </p>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
            <div className="flex items-center space-x-3 mb-4">
              <FaBrain className="text-[#FFD700]" size={24} />
              <h3 className="text-xl font-bold text-[#FFD700]">✨ AI-Powered Insights</h3>
            </div>

            {loadingAI ? (
              <div className="flex items-center justify-center py-8">
                <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
              </div>
            ) : aiInsights ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-white font-semibold mb-2">Interpretation</h4>
                  <p className="text-gray-300">{aiInsights.interpretation}</p>
                </div>

                <div>
                  <h4 className="text-white font-semibold mb-2">Investment Recommendations</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-300">
                    {aiInsights.recommendations?.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-white font-semibold mb-2">Risk Mitigation Strategies</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-300">
                    {aiInsights.risk_mitigation?.map((strategy, idx) => (
                      <li key={idx}>{strategy}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <button
                onClick={fetchAIInsights}
                className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
              >
                Get AI Insights
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskProfiler;